// WS-75 (ground lane): node-material patches that compose (each hook adds to the material's own build and its program
// cache key). Several modules shade one material (the ground: snow shading, the
// bare-ground layers, water, snow relief, the far canopy, light casting...), each without knowing the others, so each
// one adds a hook at a STAGE of the material's own build instead of replacing a node slot:
//   onColor(m, fn(builder), stage)      after diffuseColor is complete (map x colour x vertex colours, opacity), before
//                                       the roughness: NodeMaterial.setupDiffuseColor. Read and assign `diffuseColor` there.
//   onNormal(m, fn(n, builder), stage)  the view-space normal after the normal map -> a new one (setupNormal)
//   onRough(m, fn(r, builder), stage)   the roughness before the geometry/AA clamp -> a new one (materialRoughness)
//   onVariants(m, fn(builder), stage)   after roughness / specular colour are set (setupVariants)
//   onLight(m, fn(ctx, builder))       after every light, before the output (gpu/lighting.js finish): ctx.reflectedLight.{directDiffuse, ...},
//                                       ctx.outgoingLight (assign it) = the lit colour before the output
//   onFog(m, fn(out, builder), stage)   the vec4 after the fog -> a new one (setupFog)
// onColor / onVariants / onLight hooks may emit statements (If, .assign) into the stage's stack; onNormal / onRough /
// onFog hooks return a node and must not: the normal is built later, inside its own sub-build, so a statement emitted
// while the hook runs lands outside it (WGSL "Recursion detected" on the normal map) - wrap statements in Fn(() => ...)().
// Hooks of one kind run in stage order (equal stages: the order they were added), so the result does not depend on
// which module patched first. Values that one stage computes and a later one needs (a normal tilt from the colour
// stage, a water mask for the roughness) go through a `property()` node assigned at the top level of the earlier hook.
// The hooks live in a WeakMap (material.userData is JSON-copied by Material.copy). A material must be a node material
// (MeshStandardNodeMaterial, ...): the renderer converts a classic material into a new object and drops the hooks.
import { materialRoughness } from 'three/tsl';
import { addLightHook, removeLightHook } from './lighting.js';

const HOOKS = new WeakMap();
const KINDS = ['color', 'normal', 'rough', 'variants', 'fog'];
let mid = 0;
// own, non-enumerable (the renderer's material key reads enumerable properties); the program key gets an id of its own:
// the hooks hold this material's own uniforms, so its program is never shared with another material's (the renderer
// shares a built program between materials with equal keys)
const own = (m, k, value) => Object.defineProperty(m, k, { configurable: true, writable: true, enumerable: false, value });

function install(m) {
  const H = Object.fromEntries(KINDS.map((k) => [k, []]));
  HOOKS.set(m, H);
  const sDiff = m.setupDiffuseColor, sNorm = m.setupNormal, sVar = m.setupVariants, sFog = m.setupFog, sKey = m.customProgramCacheKey;
  const id = ++mid;
  own(m, 'customProgramCacheKey', function () { return `${sKey.call(this)}|ws75h${id}`; });
  own(m, 'setupDiffuseColor', function (b) { sDiff.call(this, b); for (const h of H.color) h.fn(b); });
  own(m, 'setupNormal', function (b) { let n = sNorm.call(this, b); for (const h of H.normal) n = h.fn(n, b); return n; });
  own(m, 'setupVariants', function (b) {
    if (H.rough.length) {
      const keep = this.roughnessNode;
      let r = keep ?? materialRoughness;
      for (const h of H.rough) r = h.fn(r, b);
      this.roughnessNode = r;
      try { sVar.call(this, b); } finally { this.roughnessNode = keep; }
    } else sVar.call(this, b);
    for (const h of H.variants) h.fn(b);
  });
  own(m, 'setupFog', function (b, out) { let o = sFog.call(this, b, out); for (const h of H.fog) o = h.fn(o, b); return o; });
  return H;
}
function add(m, kind, fn, stage) {
  const L = (HOOKS.get(m) || install(m))[kind];
  L.push({ fn, stage });
  L.sort((a, b) => a.stage - b.stage); // stable: equal stages keep their order
  m.needsUpdate = true;
  return fn;
}
export const onColor = (m, fn, stage = 10) => add(m, 'color', fn, stage);
export const onNormal = (m, fn, stage = 10) => add(m, 'normal', fn, stage);
export const onRough = (m, fn, stage = 10) => add(m, 'rough', fn, stage);
export const onVariants = (m, fn, stage = 10) => add(m, 'variants', fn, stage);
// light hooks go through the materials lane's gpu/lighting.js (the one shared lighting hook: snow shading, cast light),
// at its `finish` stage (after the sum, in the order added; cast light enters earlier, at its `lights` stage)
const LIGHT_KEYS = new WeakMap();
export const onLight = (m, fn) => {
  const key = pname('ws75-light-');
  addLightHook(m, { key, finish: (c) => fn(c, c.builder) });
  LIGHT_KEYS.set(fn, key);
  return fn;
};
export const onFog = (m, fn, stage = 10) => add(m, 'fog', fn, stage);
/** Takes hooks off again (a look that is patched anew, e.g. the editor's snow look): every hook whose fn is in `fns`. */
export function offHooks(m, fns) {
  for (const fn of fns) if (LIGHT_KEYS.has(fn)) removeLightHook(m, LIGHT_KEYS.get(fn));
  const H = HOOKS.get(m);
  if (!H) return;
  const drop = new Set(fns);
  for (const k of KINDS) H[k] = H[k].filter((h) => !drop.has(h.fn));
  m.needsUpdate = true;
}

/** Per-material extension points another module reads at build time (e.g. SEAMS.get(farLandMaterial).canopyK: the far
 *  canopy's share of the pixel, a property node, for the trees lane's far-forest haze). */
export const SEAMS = new WeakMap();
export function seam(m) { let s = SEAMS.get(m); if (!s) SEAMS.set(m, (s = {})); return s; }

let uid = 0;
/** A unique name for a property() node (one per patched material and value). */
export const pname = (base) => `${base}${++uid}`;
